import React from 'react'
import { differenceInDays } from 'date-fns'
import { Box, Button, Icon, Widget, Link } from '@maui/elements'

const Heading = ({ children }) => (
  <Box
    width='fit-content'
    display='inline-block'
    as='span'
    mr='spacePaddingMedium'
    fontWeight='700'
    color='colorTextDeemphasis'
    whiteSpace='nowrap'
    lineHeight="20px"
  >
    {children}
  </Box>
)

const Content = ({ children }) => (
  <Box
    as='span'
    fontSize='textBody1FontSize'
    fontWeight='700'
    color='colorTextMain'
    lineHeight="20px"
    mr='spaceGapLarge'
  >
    {children}
  </Box>
)

const FullDetails = ({ children, onClick }) => (
  <Button
    width="fit-content"
    p="0"
    variant="primaryText"
    fontSize='textBody2FontSize'
    lineHeight="20px"
    onClick={onClick}
  >
    {children}
  </Button>
)

const Section = ({ title, items, onClick, buttonLabel, icon, type }) => {
  const names = items.map(i => i.name).join(', ')
  return (
    <Box mt='spaceGapLarge'>
      <Heading>{title}</Heading>
      {type === 'campaign'
        ? items.map((item, i) =>
          <Link
            as="a"
            target="_blank"
            key={item.id}
            href={`/campaigns/${item.id}`}
          >
            <Content>
              {item.name}
              {i !== items.length - 1 && ','}
            </Content>
          </Link>
        )
        : <Content>{names}</Content>
      }
      <FullDetails onClick={onClick}>
        {buttonLabel}
        <Icon name={icon} size='sm' />
      </FullDetails>
    </Box>
  )
}

const SectionCheck = ({ items, titlePrefix, onClickHandler, buttonLabel, icon, shouldShow, type }) => {
  if (items?.length > 0 && shouldShow) {
    return (
      <Section
        title={`${items.length} ${titlePrefix}`}
        items={items}
        onClick={onClickHandler}
        buttonLabel={buttonLabel}
        icon={icon}
        type={type}
      />
    )
  }
  return null
}

export const Divider = (props) => (
  <Box
    backgroundColor='colorBorderDeemphasis'
    height='1px'
    marginTop='-8px'
    {...props}
  />
)

const filterRecentItems = (items, key, useDirectDate = false) =>
  items?.map(item => {
    const dateToCheck = useDirectDate ? item.date : (item[key] ? item[key] : item.first_seen)
    return isNew(dateToCheck) ? item : undefined
  }).filter(item => item !== undefined)

export const isNew = (date) => differenceInDays(new Date(), new Date(date)) <= 30

function RecentActivity({ id, pageData, actorCampaigns, pageReports, type, inModal = false, scrollToAndSetExpanded }) {
  if (!pageData && !pageReports) return null
  const {
    industries,
    malware,
    actors,
  } = pageData

  const { campaigns } = actorCampaigns || {}

  const handleReportsClick = () => {
    if (!inModal) {
      window.history.pushState({}, '', `/${type}/${id}#reports`)
    } else {
      window.location.href = `/${type}/${id}#reports`
    }
  }

  const handleMalwaresClick = () => scrollToAndSetExpanded('associationsList', 'malwareList')
  const handleIndustriesClick = () => scrollToAndSetExpanded('targetsList', 'industriesList')
  const handleActorsClick = () => scrollToAndSetExpanded('associationsList', 'actorsList')

  const recentIndustries = filterRecentItems(industries, 'last_seen')
  const recentMalwares = filterRecentItems(malware, 'last_seen')
  const recentActors = filterRecentItems(actors, 'last_seen')
  const recentReports = filterRecentItems(pageReports, null, true)
  const recentCampaigns = filterRecentItems(campaigns, 'profile_updated')

  const shouldShowRecentActivity = [recentIndustries, recentMalwares, recentReports, recentActors, recentCampaigns].some(arr => arr?.length > 0)
  if (!shouldShowRecentActivity) return null

  return (
    <Widget title='Recent Activity' mb="spaceGapXLarge">
      <SectionCheck
        items={recentCampaigns}
        shouldShow
        titlePrefix="New Associated Campaign Sighting:"
        type='campaign'
      />
      <SectionCheck
        items={recentReports}
        shouldShow
        titlePrefix="New Relevant Reports:"
        onClickHandler={handleReportsClick}
        buttonLabel="View Relevant Reporting"
      />
      <SectionCheck
        items={recentActors}
        shouldShow
        titlePrefix="New Associated Actors Sightings:"
        onClickHandler={handleActorsClick}
        buttonLabel="Scroll To Full Details"
        icon='arrow-down'
      />
      <SectionCheck
        items={recentMalwares}
        shouldShow
        titlePrefix="New Associated Malware Sightings:"
        onClickHandler={handleMalwaresClick}
        buttonLabel="Scroll To Full Details"
        icon='arrow-down'
      />
      <SectionCheck
        items={recentIndustries}
        shouldShow={type === 'actors'}
        titlePrefix="New Targeted Industries sightings:"
        onClickHandler={handleIndustriesClick}
        buttonLabel="Scroll To Full Details"
        icon='arrow-down'
      />
    </Widget>
  )
}

export default RecentActivity
